import {
  AddressInput,
  EmailInput,
  MaskedInput,
  SingleSelect,
  TextInput,
} from '@hourly-hds/components';
import { z } from 'zod';
import { US_STATES_OPTIONS } from '~/constants/states';
import {
  FederalTaxClassification,
  TaxPayerIdentificationType,
} from '../../data/models/partner.model';
import styles from './PartnerDetailForm.module.css';

const FEDERAL_TAX_CLASSIFICATION_FOR_EIN_OPTIONS: Array<{
  value: FederalTaxClassification;
  label: string;
}> = [
  { value: FederalTaxClassification.Individual, label: 'Individual/sole Proprietor' },
  { value: FederalTaxClassification.CCorporation, label: 'C Corporation' },
  { value: FederalTaxClassification.SCorporation, label: 'S Corporation' },
  { value: FederalTaxClassification.Partnership, label: 'Partnership' },
  { value: FederalTaxClassification.TrustEstate, label: 'Trust/Estate' },
  { value: FederalTaxClassification.LLCAsCCorporation, label: 'LLC classified as C Corporation' },
  { value: FederalTaxClassification.LLCAsSCorporation, label: 'LLC classified as S Corporation' },
  { value: FederalTaxClassification.LLCAsPartnership, label: 'LLC classified as Partnership' },
  { value: FederalTaxClassification.Other, label: 'Other' },
];

const FEDERAL_TAX_CLASSIFICATION_FOR_SSN_OPTIONS: Array<{
  value: FederalTaxClassification;
  label: string;
}> = [{ value: 'Individual', label: 'Individual/sole Proprietor' }];

const TAXPAYER_IDENTIFICATION_TYPE_OPTIONS: Array<{
  value: TaxPayerIdentificationType;
  label: string;
}> = [
  { value: TaxPayerIdentificationType.SocialSecurityNumber, label: 'Social Security Number (SSN)' },
  {
    value: TaxPayerIdentificationType.EmployerIdentificationNumber,
    label: 'Employer Identification Number (EIN)',
  },
];

const EIN_MASK = '00-0000000';
const SSN_MASK = '000-00-0000';

const TAXPAYER_IDENTIFICATION_NUMBER_BLACKLIST = [
  '123456789',
  '987654321',
  '000000000',
  '111111111',
  '222222222',
  '333333333',
  '444444444',
  '555555555',
  '666666666',
  '777777777',
  '888888888',
  '999999999',
];

const SSN_CONSTRAINTS = /^(?!000)(?!\d{3}00)(?!\d{5}0000)(?!666)\d{9}$/;

const isTaxpayerIdentificationNumberBlacklisted = (
  taxpayerIdentificationNumber: string
): boolean => {
  return !TAXPAYER_IDENTIFICATION_NUMBER_BLACKLIST.includes(taxpayerIdentificationNumber);
};

const partnerSchema = z
  .object({
    legalName: z.string().min(1, { message: 'Legal name is required.' }),
    dba: z.string().optional(),
    taxpayerIdentificationType: z
      .string()
      .min(1, { message: 'Taxpayer identification type is required.' }),
    taxpayerIdentificationNumber: z
      .string()
      .min(1, { message: 'Taxpayer identification number is required.' })
      .refine(isTaxpayerIdentificationNumberBlacklisted, {
        message: 'Taxpayer identification number is not allowed.',
      }),
    federalTaxClassification: z
      .string()
      .min(1, { message: 'Federal tax classification is required.' }),
    firstName: z.string().min(1, { message: 'First name is required.' }),
    lastName: z.string().min(1, { message: 'Last name is required.' }),
    email: z.email().min(1, { message: 'Email is required.' }),
    phoneNumber: z.string().min(10, { message: 'Phone number must be at least 10 digits.' }),
    address: z.string().min(1, { message: 'Address is required.' }),
    address2: z.string().optional(),
    city: z.string().min(1, { message: 'City is required.' }),
    state: z.string().min(1, { message: 'State is required.' }),
    zipCode: z.string().min(1, { message: 'Zip code is required.' }),
  })
  .superRefine(({ taxpayerIdentificationType, taxpayerIdentificationNumber }, ctx) => {
    if (taxpayerIdentificationType === TaxPayerIdentificationType.SocialSecurityNumber) {
      const isSsnValid = SSN_CONSTRAINTS.test(taxpayerIdentificationNumber);

      if (!isSsnValid) {
        ctx.addIssue({
          code: 'custom',
          message: 'Invalid Social Security Number format.',
          path: ['taxpayerIdentificationNumber'],
        });
      }
    }
  });

export type PartnerForm = z.infer<typeof partnerSchema>;
export type PartnerFormError = Record<Partial<keyof PartnerForm>, Array<string>>;

export const partnerDetailFormInitialValues: PartnerForm = {
  legalName: '',
  dba: '',
  taxpayerIdentificationType: '',
  taxpayerIdentificationNumber: '',
  federalTaxClassification: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
};

export const validateForm = (form: PartnerForm): PartnerFormError | null => {
  const formValidation = partnerSchema.safeParse(form);
  if (formValidation.success) {
    return null;
  }

  const formErrors = z.flattenError(formValidation.error);
  return formErrors.fieldErrors as PartnerFormError;
};

interface PartnerDetailsFormProps {
  partner: PartnerForm;
  errors: Partial<PartnerFormError> | null;
  disabled: boolean;
  onChange: (changes: Partial<PartnerForm>) => void;
}

const PartnerDetailForm = ({
  partner = partnerDetailFormInitialValues,
  errors,
  disabled = false,
  onChange,
}: PartnerDetailsFormProps) => {
  const getError = (field: keyof PartnerForm): string | undefined => {
    return errors?.[field]?.[0];
  };

  const federalTaxClassificationOptions =
    partner.taxpayerIdentificationType === TaxPayerIdentificationType.EmployerIdentificationNumber
      ? FEDERAL_TAX_CLASSIFICATION_FOR_EIN_OPTIONS
      : FEDERAL_TAX_CLASSIFICATION_FOR_SSN_OPTIONS;

  const taxpayerIdentificationNumberMask =
    partner.taxpayerIdentificationType === TaxPayerIdentificationType.EmployerIdentificationNumber
      ? EIN_MASK
      : SSN_MASK;

  return (
    <div className={styles.form}>
      <TextInput
        label="Partner Legal Name"
        value={partner.legalName}
        onChange={(legalName) => onChange({ legalName })}
        description={getError('legalName')}
        error={!!getError('legalName')}
        disabled={disabled}
      />
      <TextInput
        label="DBA"
        value={partner.dba}
        onChange={(dba) => onChange({ dba })}
        description={getError('dba')}
        error={!!getError('dba')}
        disabled={disabled}
      />
      <SingleSelect
        label="Taxpayer Identification Type"
        options={TAXPAYER_IDENTIFICATION_TYPE_OPTIONS}
        value={partner.taxpayerIdentificationType}
        onChange={(taxpayerIdentificationType) => {
          if (taxpayerIdentificationType === TaxPayerIdentificationType.SocialSecurityNumber) {
            onChange({
              taxpayerIdentificationType,
              federalTaxClassification: FederalTaxClassification.Individual,
            });
            return;
          }

          onChange({ taxpayerIdentificationType });
        }}
        description={getError('taxpayerIdentificationType')}
        error={!!getError('taxpayerIdentificationType')}
        disabled={disabled}
      />
      <MaskedInput
        label="Taxpayer Identification Number"
        mask={taxpayerIdentificationNumberMask}
        value={partner.taxpayerIdentificationNumber}
        onChange={(taxpayerIdentificationNumber) => onChange({ taxpayerIdentificationNumber })}
        description={getError('taxpayerIdentificationNumber')}
        error={!!getError('taxpayerIdentificationNumber')}
        disabled={disabled}
      />
      <SingleSelect
        label="Federal Tax Classification"
        options={federalTaxClassificationOptions}
        value={partner.federalTaxClassification}
        onChange={(federalTaxClassification) => onChange({ federalTaxClassification })}
        description={getError('federalTaxClassification')}
        error={!!getError('federalTaxClassification')}
        disabled={
          partner.taxpayerIdentificationType === TaxPayerIdentificationType.SocialSecurityNumber ||
          disabled
        }
      />
      <TextInput
        label="First Name"
        value={partner.firstName}
        onChange={(firstName) => onChange({ firstName })}
        description={getError('firstName')}
        error={!!getError('firstName')}
        disabled={disabled}
      />
      <TextInput
        label="Last Name"
        value={partner.lastName}
        onChange={(lastName) => onChange({ lastName })}
        description={getError('lastName')}
        error={!!getError('lastName')}
        disabled={disabled}
      />
      <EmailInput
        disabled
        label="Email"
        value={partner.email}
        onChange={(email) => onChange({ email })}
        description={getError('email')}
        error={!!getError('email')}
      />
      <MaskedInput
        disabled
        label="Mobile Number"
        mask="(000) 000-0000"
        value={partner.phoneNumber}
        onChange={(phoneNumber) => onChange({ phoneNumber })}
        description={getError('phoneNumber')}
        error={!!getError('phoneNumber')}
      />
      <AddressInput
        label="Physical Address"
        description={getError('address') ? getError('address') : 'P.O. Box not allowed'}
        value={partner.address}
        onChange={(address) => onChange({ address })}
        onSelectPlace={() => {}}
        error={!!getError('address')}
        disabled={disabled}
      />
      <TextInput
        label="Address line 2 (Apt #, Suite #, etc)"
        value={partner.address2}
        onChange={(address2) => onChange({ address2 })}
        description={getError('address2')}
        error={!!getError('address2')}
        disabled={disabled}
      />
      <TextInput
        label="City"
        value={partner.city}
        onChange={(city) => onChange({ city })}
        description={getError('city')}
        error={!!getError('city')}
        disabled={disabled}
      />
      <SingleSelect
        label="State"
        options={US_STATES_OPTIONS}
        value={partner.state}
        onChange={(state) => onChange({ state })}
        description={getError('state')}
        error={!!getError('state')}
        disabled={disabled}
      />
      <MaskedInput
        label="Zip Code"
        mask="000000000"
        value={partner.zipCode}
        onChange={(zipCode) => onChange({ zipCode })}
        description={getError('zipCode')}
        error={!!getError('zipCode')}
        disabled={disabled}
      />
    </div>
  );
};

export default PartnerDetailForm;
